import React, { useContext, useEffect, useState } from "react";
import { Card, Typography, Box, useTheme, alpha } from "@mui/material";
import OAuthService from "src/app/auth/services/oAuthService";
import { SelectedStatus } from "./OrderList";
import jwtDecode from "jwt-decode";
import { useDispatch, useSelector } from "react-redux";
import { setHasFilter, setSavedFilterCardStatus } from "app/store/filterSlice";

import FiberNewOutlinedIcon from "@mui/icons-material/FiberNewOutlined";
import PauseCircleOutlinedIcon from "@mui/icons-material/PauseCircleOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import RuleFolderOutlinedIcon from "@mui/icons-material/RuleFolderOutlined";


const THEME = {
  blue: { accent: "#4C6FFF" },
  green: { accent: "#22B07D" },
  orange: { accent: "#F5921B" },
  purple: { accent: "#8B5CF6" },
  lightBlue: { accent: "#2C9FE0" },
  pink: { accent: "#F14B6B" },
  teal: { accent: "#16A79A" },
};


const getCardColors = (accent, mode) => ({
  cardBg: alpha(accent, mode === "dark" ? 0.16 : 0.07),
  iconBg: alpha(accent, mode === "dark" ? 0.32 : 0.15),
});

const Cards = (props) => {
  const theme = useTheme();
  const token = localStorage && localStorage.getItem("token") && jwtDecode(localStorage.getItem("token"));
  const savedFilter = useSelector((state) => state.filter.cardStatus);
  const [data, setData] = useState();
  const [selectedStatus, setSelectedStatus] = useContext(SelectedStatus);
  const [news, setNews] = useState(0);
  const [inProgress, setInProgress] = useState(0);
  const [completed, setCompleted] = useState(0);
  const [hold, setHold] = useState(0);
  const [newAb, setNewAb] = useState(0);
  const [revisedAb, setRevisedAb] = useState(0);
  const resourceUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}`;
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchCounts = async () => {
      if (props.hasCustomerRole) {
        const [types, countNewAb, countRevisedAb] = await Promise.all([
          OAuthService.getList(resourceUrl + "api/getCustomerOrderCount"),
          OAuthService.getList(resourceUrl + `api/orders/search/countCustomerNewAb?customerId=${token?.company}`),
          OAuthService.getList(resourceUrl + `api/orders/search/countCustomerRevisedAb?customerId=${token?.company}`)
        ]);

        setNewAb(countNewAb.data);
        setRevisedAb(countRevisedAb.data);

        const newCount = types.data.find((item) => item._id === "NEW");
        setNews(newCount?.count ? newCount?.count : 0);

        const inProgressCount = types.data.find((item) => item._id === "IN_PROGRESS");
        setInProgress(inProgressCount?.count ? inProgressCount?.count : 0);

        const completedCount = types.data.find((item) => item._id === "COMPLETED");
        setCompleted(completedCount?.count ? completedCount?.count : 0);

        const holdCount = types.data.find((item) => item._id === "HOLD");
        setHold(holdCount?.count ? holdCount?.count : 0);
      } else {
        const types = await OAuthService.getList(resourceUrl + "api/diffOrderCount");
        setData(types.data);
      }
    };
    fetchCounts();
  }, [props.hasCustomerRole]);

  const cardConfig = props.hasCustomerRole
    ? [
        { id: "new", label: "New", count: news, icon: FiberNewOutlinedIcon, theme: THEME.blue },
        { id: "hold", label: "On Hold", count: hold, icon: PauseCircleOutlinedIcon, theme: THEME.orange },
        { id: "progress", label: "In Progress", count: inProgress, icon: PendingActionsOutlinedIcon, theme: THEME.lightBlue },
        { id: "complete", label: "Orders Placed", count: completed, icon: TaskAltOutlinedIcon, theme: THEME.green },
        { id: "newAb", label: "New ABs", count: newAb, icon: DescriptionOutlinedIcon, theme: THEME.purple },
        { id: "revisedAb", label: "Revised ABs", count: revisedAb, icon: AssignmentTurnedInOutlinedIcon, theme: THEME.pink }
      ]
    : [
        { id: "abWaiting", label: "AB Waiting", count: data?.abWaiting || 0, icon: HourglassEmptyOutlinedIcon, theme: THEME.orange },
        { id: "unConfirmed", label: "Un Confirmed", count: data?.unConfirmed || 0, icon: VerifiedOutlinedIcon, theme: THEME.lightBlue },
        { id: "unBooked", label: "Unbooked", count: data?.unBooked || 0, icon: EventAvailableOutlinedIcon, theme: THEME.pink },
        { id: "booked", label: "Booked", count: data?.booked || 0, icon: ReceiptLongOutlinedIcon, theme: THEME.blue },
        { id: "readyToPi", label: "Ready to PI", count: data?.readyToPi || 0, icon: RuleFolderOutlinedIcon, theme: THEME.purple, hide: props.hasPartnerRole || props.hasPartnerAdminRole || props.hasShwrmMngrRole },
        { id: "notLoaded", label: "Not Loaded", count: data?.notLoaded || 0, icon: Inventory2OutlinedIcon, theme: THEME.teal },
        { id: "loaded", label: "Loaded", count: data?.loaded || 0, icon: LocalShippingOutlinedIcon, theme: THEME.green },
        { id: "unreconciled", label: "Unreconciled", count: data?.unreconciled || 0, icon: Inventory2Icon, theme: THEME.orange }
      ];

  const handleCardClick = (id) => {
    dispatch(setSavedFilterCardStatus(id));
    dispatch(setHasFilter(true));
    setSelectedStatus(id);
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:flex lg:flex-row gap-4 w-full">
      {cardConfig
        .filter((card) => !card.hide)
        .map((card) => {
          const isActive = savedFilter === card.id;
          const Icon = card.icon;
          const { accent } = card.theme;
          const { cardBg, iconBg } = getCardColors(accent, theme.palette.mode);

          return (
            <Card
              key={card.id}
              onClick={() => handleCardClick(card.id)}
              elevation={0}
              className="lg:flex-1 lg:min-w-0"
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                p: 1.5,
                borderRadius: 3,
                cursor: "pointer",
                overflow: "hidden",
                bgcolor: cardBg,
                borderLeft: `4px solid ${accent}`,
                boxShadow: isActive
                  ? `0 0 0 1.5px ${accent}, 0 6px 14px rgba(16,24,40,0.12)`
                  : "0 1px 4px rgba(16,24,40,0.06)",
                transform: isActive ? "translateY(-1px)" : "none",
                transition: "transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease",
                "&:hover": {
                  boxShadow: `0 0 0 1.5px ${accent}, 0 6px 14px rgba(16,24,40,0.1)`,
                },
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  width: 34,
                  height: 34,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: iconBg,
                }}
              >
                <Icon sx={{ fontSize: 18, color: accent }} />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  className="tracking-wider uppercase"
                  sx={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: "text.secondary",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {card.label}
                </Typography>
                <Typography sx={{ fontSize: 16, fontWeight: 700, mt: 0.25 }}>
                  {card.count}
                </Typography>
              </Box>
            </Card>
          );
        })}
    </div>
  );
};

export default Cards;